import { useCallback, useState } from "react";
import { Api } from "services/api/api.service";
import { buildApprovePayload } from "../models/personView.actions";
import type { BulkAction } from "../models/personView.actions";
import type { PersonDetailSelection } from "../models/personView.types";

export type FeedbackState =
  | { type: "success" }
  | { type: "partial"; succeededCount: number; failedCount: number }
  | { type: "error" }
  | null;

type CommentableBulkAction = Exclude<BulkAction, "absence">;

interface UsePersonBulkActionsParams {
  companyId: number | undefined;
  selectedDetails: PersonDetailSelection[];
  openedDetail: PersonDetailSelection | null;
  selectedDetailIdSet: Set<number>;
  clearSelection: () => void;
  retainSelectionByIds: (ids: Set<number>) => void;
  setOpenedDetail: (selection: PersonDetailSelection | null) => void;
  reload: () => void;
}

interface UsePersonBulkActionsState {
  canRunActions: boolean;
  isBulkActionLoading: boolean;
  isAbsenceModalOpen: boolean;
  absenceTargetIds: number[];
  bulkCommentAction: CommentableBulkAction | null;
  feedbackState: FeedbackState;
  runBulkAction: (action: BulkAction) => Promise<void>;
  runCommentableBulkAction: (
    action: CommentableBulkAction,
    comment: string,
  ) => Promise<boolean>;
  closeAbsenceModal: () => void;
  handleAbsenceReported: (payload: {
    succeededCount: number;
    failedCount: number;
    failedIds: number[];
  }) => void;
  closeBulkCommentModal: () => void;
  clearFeedback: () => void;
  showActionError: () => void;
}

const runSelectionAction = async (
  action: CommentableBulkAction,
  companyId: number,
  selection: PersonDetailSelection,
  comment: string,
): Promise<void> => {
  const timeReportsApi = Api().company.timeReports;
  if (action === "retract") {
    await timeReportsApi.withdrawTimeReport(companyId, selection.item.id, comment);
    return;
  }
  if (action === "reset") {
    await timeReportsApi.reopenTimeReport(companyId, selection.item.id, comment);
    return;
  }
  if (action === "reject") {
    await timeReportsApi.rejectTimeReport(companyId, selection.item.id, comment);
    return;
  }

  await timeReportsApi.approveTimeReport(companyId, selection.item.id, {
    ...buildApprovePayload(selection.item),
    comment,
  });
};

export const usePersonBulkActions = (
  params: UsePersonBulkActionsParams,
): UsePersonBulkActionsState => {
  const {
    companyId,
    selectedDetails,
    openedDetail,
    selectedDetailIdSet,
    clearSelection,
    retainSelectionByIds,
    setOpenedDetail,
    reload,
  } = params;

  const [isBulkActionLoading, setIsBulkActionLoading] = useState(false);
  const [isAbsenceModalOpen, setIsAbsenceModalOpen] = useState(false);
  const [absenceTargetIds, setAbsenceTargetIds] = useState<number[]>([]);
  const [bulkCommentAction, setBulkCommentAction] =
    useState<CommentableBulkAction | null>(null);
  const [feedbackState, setFeedbackState] = useState<FeedbackState>(null);

  const canRunActions = Boolean(companyId && selectedDetails.length > 0);

  const showActionSuccess = useCallback((): void => {
    setFeedbackState({ type: "success" });
  }, []);

  const showActionError = useCallback((): void => {
    setFeedbackState({ type: "error" });
  }, []);

  const runCommentableBulkAction = useCallback(
    async (
      action: CommentableBulkAction,
      comment: string,
    ): Promise<boolean> => {
      if (!canRunActions || isBulkActionLoading || !companyId) {
        return false;
      }

      setIsBulkActionLoading(true);
      try {
        let succeededCount = 0;
        let failedCount = 0;
        const failedIds = new Set<number>();

        for (const selection of selectedDetails) {
          try {
            await runSelectionAction(action, companyId, selection, comment);
            succeededCount += 1;
          } catch {
            failedCount += 1;
            failedIds.add(selection.item.id);
          }
        }

        if (failedCount === 0) {
          showActionSuccess();
          clearSelection();
        } else if (succeededCount === 0) {
          showActionError();
        } else {
          setFeedbackState({ type: "partial", succeededCount, failedCount });
          retainSelectionByIds(failedIds);
        }

        if (
          openedDetail &&
          selectedDetailIdSet.has(openedDetail.item.id) &&
          !failedIds.has(openedDetail.item.id)
        ) {
          setOpenedDetail(null);
        }
      } finally {
        reload();
        setIsBulkActionLoading(false);
      }

      return true;
    },
    [
      canRunActions,
      companyId,
      clearSelection,
      isBulkActionLoading,
      reload,
      retainSelectionByIds,
      openedDetail,
      selectedDetailIdSet,
      selectedDetails,
      setOpenedDetail,
      showActionError,
      showActionSuccess,
    ],
  );

  const runBulkAction = useCallback(
    async (action: BulkAction): Promise<void> => {
      if (!canRunActions || isBulkActionLoading || !companyId) return;

      if (action === "absence") {
        setAbsenceTargetIds(selectedDetails.map((selection) => selection.item.id));
        setIsAbsenceModalOpen(true);
        return;
      }

      setBulkCommentAction(action);
    },
    [canRunActions, companyId, isBulkActionLoading, selectedDetails],
  );

  const closeAbsenceModal = useCallback((): void => {
    setIsAbsenceModalOpen(false);
    setAbsenceTargetIds([]);
  }, []);

  const handleAbsenceReported = useCallback(
    (payload: {
      succeededCount: number;
      failedCount: number;
      failedIds: number[];
    }): void => {
      const { succeededCount, failedCount, failedIds } = payload;
      closeAbsenceModal();
      if (failedCount === 0) {
        clearSelection();
        showActionSuccess();
    } else {
      setFeedbackState({ type: "partial", succeededCount, failedCount });
      retainSelectionByIds(new Set(failedIds));
    }
    if (
      openedDetail &&
      selectedDetailIdSet.has(openedDetail.item.id) &&
      !failedIds.includes(openedDetail.item.id)
    ) {
      setOpenedDetail(null);
    }
    reload();
    },
    [
      clearSelection,
      closeAbsenceModal,
      reload,
      retainSelectionByIds,
      openedDetail,
      selectedDetailIdSet,
      setOpenedDetail,
      showActionSuccess,
    ],
  );

  const closeBulkCommentModal = useCallback((): void => {
    setBulkCommentAction(null);
  }, []);

  const clearFeedback = useCallback((): void => {
    setFeedbackState(null);
  }, []);

  return {
    canRunActions,
    isBulkActionLoading,
    isAbsenceModalOpen,
    absenceTargetIds,
    bulkCommentAction,
    feedbackState,
    runBulkAction,
    runCommentableBulkAction,
    closeAbsenceModal,
    handleAbsenceReported,
    closeBulkCommentModal,
    clearFeedback,
    showActionError,
  };
};
